import React, {
  useState,
  useEffect,
  useContext,
  useRef,
  useImperativeHandle,
} from 'react';
import { Form, FormGroup } from 'reactstrap';
import { GlobalContext } from '../../api/GlobalContext';
import Modal from '../../components/Modals/Modal';
import { startScreenCapture } from '../../EMR_Integration/react_webInjector.ts';
import getDeviceData from '../../api/DeviceInfo';
import {
  checkpointForBugReport,
  endBugReportCapture,
  getActiveReplayMetadata,
  startBugReportReplay,
} from '../../api/replay';
import { createReplayLink } from '../../views/replay/replayLink';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate } from 'react-router-dom';

// The replay viewer only exists in the web app, and this component also runs
// from chrome-extension:// in the side panel, so the link's origin is fixed
// rather than taken from window.location.
const REPLAY_VIEWER_ORIGIN = 'https://app.comprehendpt.com';

const ZENDESK_SNIPPET_SRC =
  'https://static.zdassets.com/ekr/snippet.js?key=62f30667-defb-44f2-8af0-cf2221ed12db';

// Opens Zendesk's own messenger on the page. Its launcher sits where "Need
// Help?" does, so the messenger is hidden again on close.
function openZendeskChat(onClose) {
  const open = () => {
    window.zE('messenger', 'show');
    window.zE('messenger', 'open');
  };
  if (window.zE) {
    open();
    return;
  }
  if (document.getElementById('ze-snippet')) return; // still loading
  const script = document.createElement('script');
  script.id = 'ze-snippet';
  script.src = ZENDESK_SNIPPET_SRC;
  script.onload = () => {
    window.zE('messenger:on', 'close', () => {
      window.zE('messenger', 'hide');
      onClose();
    });
    open();
  };
  document.body.appendChild(script);
}

function formatReplayOffset(offsetMs) {
  const totalSeconds = Math.round(offsetMs / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  return `${minutes}:${String(totalSeconds % 60).padStart(2, '0')}`;
}

const FloatingBugButton = React.forwardRef((_, ref) => {
  const { apiClient } = useContext(GlobalContext);
  const [isHovered, setIsHovered] = useState(false);
  const [isClicked, setIsClicked] = useState(false);
  const [videoUrl, setVideoUrl] = useState('');
  const [showRecordingModal, setShowRecordingModal] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [showChatIframe, setShowChatIframe] = useState(false);
  const [issueDescription, setIssueDescription] = useState('');
  const chunksRef = useRef([]); // Persistent storage for chunks
  const consoleLogsRef = useRef([]); // Rolling buffer of EMR/extension console logs
  const replayRangeRef = useRef({ start: null, end: null });
  const [stream, setStream] = useState(null);
  const [uploadUrl, setUploadUrl] = useState('');
  const [recorder, setRecorder] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [isProcessingRecording, setIsProcessingRecording] = useState(false);
  const isCX = window.location.protocol === 'chrome-extension:';
  const zendeskChat = !!apiClient?.features?.['zendesk_support_chat'];
  const [zendeskOpen, setZendeskOpen] = useState(false);
  // Demo Playground sandboxes (LambdaAPI /v1/demo/* mints them as
  // <id>@demo.comprehendpt.ai) are prospects poking at a disposable account,
  // not customers — no Support Chat and no "Need Help?" button at all: the
  // Playground carries its own guidance, and the "?" over a sales demo reads
  // as a loose end. (The imperative trigger still works if a host asks.)
  const isDemoSandboxUser = /@demo\.comprehendpt\.ai$/i.test(
    apiClient?.email || apiClient?.sessionEmail || '',
  );

  const handleMouseEnter = () => setIsHovered(true);
  const handleMouseLeave = () => setIsHovered(false);
  const navigate = useNavigate();

  const handleBugButtonClick = () => {
    if (isDemoSandboxUser) {
      setShowRecordingModal(true);
      return;
    }
    setShowOptionsModal(true);
  };

  useImperativeHandle(ref, () => ({
    triggerBugButton: () => handleBugButtonClick(),
  }));

  /*
  This useEffect listens for messages sent from the React Web Injector, 
  which informs the application that the screen recording video has been successfully uploaded to S3.
  
  Purpose:
  For Chrome Extension and Mic Drop workflows.
  It provides the URL of the uploaded video to enable preview functionality.

  How It Works:
  - Adds a message event listener to the window object.
  - When a message with type: 'SCREEN_RECORDING_RESULT' is received, 
  it extracts the video URL (event.data.payload.url) and sets it in videoUrl for preview.
  - Cleans up the event listener when the component unmounts.
  This mechanism ensures the app can seamlessly display the uploaded video for review in the extension-based recording workflows.
   */

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.data && event.data.type === 'SCREEN_RECORDING_RESULT') {
        const blobUrl = event.data.payload.url;
        replayRangeRef.current.end = getActiveReplayMetadata();
        // The capture is over. A recording started for this report runs on
        // for its tail from here, whether or not the report is then sent.
        endBugReportCapture().catch(() => {});
        setIsProcessingRecording(false);
        setVideoUrl(blobUrl); // For preview
      }
    };

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  // Capture console errors/warnings from the EMR content script (CX only)
  useEffect(() => {
    // eslint-disable-next-line no-undef
    const chromeRuntime = typeof chrome !== 'undefined' && chrome.runtime;
    // On an ordinary page window.chrome.runtime can exist without onMessage (only extension pages have it).
    if (!isCX || !chromeRuntime || !chromeRuntime.onMessage) return;

    const handleExtensionMessage = (request) => {
      if (request.type === 'SCREEN_RECORDING_PROCESSING') {
        setIsProcessingRecording(true);
        return;
      }
      if (request.type === 'CONSOLE_ERROR') {
        consoleLogsRef.current.push({
          timestamp: new Date().toISOString(),
          ...request.log,
        });
        if (consoleLogsRef.current.length > 50) {
          consoleLogsRef.current.shift();
        }
      }
    };

    chromeRuntime.onMessage.addListener(handleExtensionMessage);
    return () => chromeRuntime.onMessage.removeListener(handleExtensionMessage);
  }, [isCX]);

  const handlePreRecordCloseButton = () => {
    setVideoUrl('');
    setIsClicked(false);
    setIsHovered(false);
  };

  const handlePostRecordCloseButton = () => {
    setIsProcessingRecording(false);
    setVideoUrl('');
    setIsClicked(false);
    setIsHovered(false);
    chunksRef.current = []; // Clear chunks
    replayRangeRef.current = { start: null, end: null };
    setRecorder(null);
    if (stream) {
      stream.getTracks().forEach((track) => track.stop());
      setStream(null);
    }
  };

  const handleGoBack = () => {
    setShowRecordingModal(true);
    setVideoUrl('');
  };

  const handleRecordingSubmit = async () => {
    try {
      replayRangeRef.current = { start: null, end: null };
      let urlResponse = '';
      if (isCX) {
        urlResponse = await apiClient.getErrorVideoUrl();
        setUploadUrl(urlResponse.url);
      }
      setShowRecordingModal(false);
      startRecording(urlResponse?.url);
    } catch (err) {
      console.error('Error preparing recording:', err);
    }
  };

  const startRecording = async (url) => {
    try {
      if (isCX) {
        // Awaited: the reproduction starts the moment the screen capture does,
        // and a replay that begins after it would miss the opening frames.
        replayRangeRef.current.start = await startBugReportReplay(apiClient);
        startScreenCapture(url);
      } else {
        chunksRef.current = []; // Clear chunks
        const displayStream = await navigator.mediaDevices.getDisplayMedia({
          video: {
            cursor: 'always',
            selfBrowserSurface: 'include',
          },
          audio: true,
        });

        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: true,
        });

        const combinedStream = new MediaStream([
          ...displayStream.getVideoTracks(),
          ...audioStream.getAudioTracks(),
        ]);

        setStream(combinedStream);

        const newRecorder = new MediaRecorder(combinedStream);

        newRecorder.ondataavailable = (e) => {
          if (e.data.size > 0) {
            chunksRef.current.push(e.data); // Add chunk
          }
        };

        newRecorder.onstop = () => {
          replayRangeRef.current.end = getActiveReplayMetadata();
          endBugReportCapture().catch(() => {});
          if (chunksRef.current.length > 0) {
            const blob = new Blob(chunksRef.current, { type: 'video/webm' });
            const url = URL.createObjectURL(blob);
            setVideoUrl(url);
          }
          setIsClicked(false);
          setIsHovered(false);
        };

        replayRangeRef.current.start = await startBugReportReplay(apiClient);
        newRecorder.start();
        setRecorder(newRecorder);

        combinedStream.getTracks().forEach((track) => {
          track.addEventListener('ended', () => {
            if (newRecorder.state === 'recording') {
              newRecorder.stop();
            }
          });
        });
      }
    } catch (err) {
      console.error('Error starting recording:', err);
    }
  };

  useEffect(() => {
    return () => {
      if (videoUrl) {
        URL.revokeObjectURL(videoUrl);
      }
    };
  }, [videoUrl]);

  const handleSendRecording = async () => {
    if (isSending) return;
    setIsSending(true);
    try {
      const deviceData = await getDeviceData();
      const channelName = 'SUPPORT_CHANNEL';
      const userInfo = {
        name: apiClient.name,
        ip_address: deviceData.IP,
        os_Type: deviceData.osType,
        os_version: deviceData.osVersion,
        browser_name: deviceData.browser,
        user_agent: deviceData.userAgent,
        app_version: deviceData.appVersion,
      };
      // A recording started for this report stops ten minutes after the capture
      // ends; a report sent later than that still names the replay it was.
      const replay = (await checkpointForBugReport()) || replayRangeRef.current.start;
      const rangeStart = replayRangeRef.current.start;
      const rangeEnd = replayRangeRef.current.end || replay;
      const rangeMatchesReplay =
        replay &&
        rangeStart?.replayId === replay.replayId &&
        rangeEnd?.replayId === replay.replayId;
      const replayRange = rangeMatchesReplay
        ? {
            ...replay,
            reproductionStartedAt: rangeStart.capturedAt,
            reproductionEndedAt: rangeEnd.capturedAt,
            reproductionStartOffsetMs: rangeStart.offsetMs,
            reproductionEndOffsetMs: rangeEnd.offsetMs,
          }
        : replay;
      // A link so the report opens straight at the moment it describes, rather
      // than an ID to paste into the viewer's search box.
      const description = replay?.replayId
        ? `${issueDescription}\n\nSession replay: ${createReplayLink(REPLAY_VIEWER_ORIGIN, replay.replayId, Date.parse(replayRange.reproductionStartedAt || replay.capturedAt))}\nReplay user: ${apiClient.sessionEmail || 'unknown'}\nReplay started: ${replay.replayStartedAt}\nReproduction: ${formatReplayOffset(replayRange.reproductionStartOffsetMs ?? replay.offsetMs)}–${formatReplayOffset(replayRange.reproductionEndOffsetMs ?? replay.offsetMs)}`
        : issueDescription;

      // Build diagnostic log attachment (API calls + EMR console logs)
      const diagnosticLog = {
        replay_id: replay?.replayId || null,
        replay: replayRange || null,
        api_calls: apiClient.getApiLog ? apiClient.getApiLog() : [],
        console_logs: consoleLogsRef.current,
      };
      const logFile = {
        content: JSON.stringify(diagnosticLog, null, 2),
        filename: `bug_report_logs_${new Date().toISOString()}.json`,
        filetype: 'json',
      };

      if (!isCX) {
        const videoBlob = new Blob(chunksRef.current, { type: 'video/webm' });
        console.log(videoBlob, issueDescription, channelName, userInfo);
        await apiClient.addErrorVideo(
          videoBlob,
          description,
          channelName,
          userInfo,
          logFile,
        );
      } else {
        const cleanUrl = uploadUrl.replace(/\?.*$/, '');
        await apiClient.sendSlackMessage(
          description,
          channelName,
          userInfo,
          cleanUrl,
          logFile,
        );
      }
      consoleLogsRef.current = [];
      setIssueDescription('');
      setShowRecordingModal(false);
      toast.info(`Bug report received! Our team will reach out soon.`);
    } catch (error) {
      console.error('Error uploading recording:', error);
      alert('Failed to upload the recording.');
    } finally {
      setIsSending(false);
    }
  };

  const containerStyle = {
    position: 'fixed',
    bottom: '20px',
    right: '20px',
    zIndex: 9999999,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
  };

  const labelStyle = {
    fontSize: '11px',
    color: '#2eccc6',
    fontWeight: '600',
    letterSpacing: '0.3px',
    userSelect: 'none',
    opacity: isHovered || isClicked ? 1 : 0,
    transition: 'opacity 0.3s ease',
    whiteSpace: 'nowrap',
  };

  const buttonStyle = {
    borderRadius: '50%',
    width: isHovered || isClicked ? '50px' : '30px',
    height: isHovered || isClicked ? '50px' : '30px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#2dce8f',
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    border: 'none',
    outline: 'none',
    boxShadow: 'none',
  };

  const iconStyle = {
    fontSize: isHovered || isClicked ? '1em' : '0.8em',
    color: 'white',
    transition: 'font-size 0.3s ease',
    textShadow: 'none',
  };

  return (
    <>
      {!isDemoSandboxUser && !zendeskOpen && (
        <div
          className="cpt-help-fab"
          style={containerStyle}
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
        >
          <span style={labelStyle}>Need Help?</span>
          <button onClick={handleBugButtonClick} style={buttonStyle}>
            <i className="fas fa-question-circle" style={iconStyle}></i>
          </button>
        </div>
      )}

      {/* Options Modal */}
      <Modal
        isOpen={showOptionsModal}
        onClose={() => setShowOptionsModal(false)}
        buttons={[
          {
            color: 'primary',
            label: 'Support Chat',
            onClick: () => {
              if (apiClient.isConfluentUser) {
                window.open(
                  'https://confluenthealthllc.freshservice.com/support/catalog/items/643',
                  '_blank',
                );
                setShowOptionsModal(false);
                return;
              }
              if (isCX) {
                navigate('/User/crisp/chat');
              } else if (zendeskChat) {
                setZendeskOpen(true);
                openZendeskChat(() => setZendeskOpen(false));
              } else {
                setShowChatIframe(true);
              }
              setShowOptionsModal(false);
            },
          },
          {
            color: 'secondary',
            label: 'Bug Report',
            onClick: () => {
              setShowOptionsModal(false);
              setShowRecordingModal(true);
            },
          },
        ]}
      >
        <h4>Choose an Option</h4>
        <p>Please select how you would like to proceed:</p>
      </Modal>

      {/* Recording Description Modal */}
      <Modal
        isOpen={showRecordingModal}
        onClose={() => {
          setShowRecordingModal(false);
          handlePreRecordCloseButton();
        }}
        buttons={[
          {
            color: 'secondary',
            label: 'Cancel',
            onClick: () => {
              setShowRecordingModal(false);
              handlePreRecordCloseButton();
            },
          },
          {
            color: 'primary',
            label: 'Record',
            onClick: handleRecordingSubmit,
            disabled: !issueDescription,
          },
        ]}
      >
        <h4>Bug Reporter</h4>
        <Form>
          <FormGroup>
            <textarea
              value={issueDescription}
              placeholder="Please describe your issue here. When you hit 'Record', walk us through the issue and try to replicate it while we capture both your screen and audio to assist you better."
              onChange={(e) => setIssueDescription(e.target.value)}
              style={{
                border: `2px solid ${issueDescription ? '#2eccc6' : '#dc3545'}`,
                borderRadius: '8px',
                padding: '10px',
                height: '300px',
                width: '100%',
                resize: 'none',
              }}
            />
          </FormGroup>
        </Form>
      </Modal>

      {/* Processing Modal - recording stopped, video still uploading */}
      <Modal
        isOpen={isProcessingRecording && !videoUrl}
        onClose={() => {}}
        hideCross={true}
        isCloseButton={false}
      >
        <h4>Processing Recording</h4>
        <p style={{ color: '#2eccc6', fontWeight: 600 }}>
          <i className="fas fa-spinner fa-spin" /> Uploading your recording -
          this can take up to a minute. Please keep this window open.
        </p>
      </Modal>

      {/* Preview Modal */}
      <Modal
        isOpen={!!videoUrl}
        onClose={() => !isSending && handlePostRecordCloseButton()}
        hideCross={isSending}
        buttons={[
          {
            color: 'danger',
            label: isCX ? 'Close' : 'Back',
            onClick: () =>
              isCX ? handlePostRecordCloseButton() : handleGoBack(),
            disabled: isSending,
          },
          {
            color: 'primary',
            label: isSending ? 'Sending...' : 'Send',
            onClick: handleSendRecording,
            disabled: isSending,
          },
        ]}
      >
        <h4>Preview Recording</h4>
        {isSending && (
          <p style={{ color: '#2eccc6', fontWeight: 600 }}>
            <i className="fas fa-spinner fa-spin" /> Sending your bug report -
            this can take up to a minute. Please keep this window open.
          </p>
        )}
        {videoUrl ? (
          <video width="100%" controls>
            <source src={videoUrl} type="video/webm" />
            Your browser does not support the video tag.
          </video>
        ) : (
          <p>Loading video preview...</p>
        )}
      </Modal>

      <Modal
        isOpen={showChatIframe}
        onClose={() => setShowChatIframe(false)}
        buttons={[
          {
            color: 'secondary',
            label: 'Close',
            onClick: () => setShowChatIframe(false),
          },
        ]}
      >
        <h4>Chat Support</h4>
        <iframe
          src={`/crisp-chat.html?name=${encodeURIComponent(apiClient.name)}&email=${encodeURIComponent(apiClient.email)}&emr=${encodeURIComponent(apiClient.EMR || '')}`}
          style={{ width: '100%', height: '80vh', border: 'none' }}
          allow="camera; microphone"
          title="Crisp Support Chat"
        />
      </Modal>

      <ToastContainer />
    </>
  );
});

export default FloatingBugButton;
